
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import AuthGuard from '../../components/feature/AuthGuard';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Artwork {
  id?: number;
  title: string;
  artist: string;
  technique: string;
  category: string;
  size: string;
  price: string;
  year: string;
  materials: string;
  description: string;
  image_url?: string;
  images?: string[];
  created_at?: string;
}

export default function AdminPage() {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState<Artwork | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<string[]>([]);
  const [formData, setFormData] = useState<Artwork>({
    title: '',
    artist: '',
    technique: 'oil',
    category: 'oil',
    size: '',
    price: '',
    year: '',
    materials: '',
    description: '',
    images: []
  });

  useEffect(() => {
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const { data, error } = await supabase
        .from('artworks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setArtworks(data || []);
    } catch (error) {
      console.error('Error fetching artworks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `artworks/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('artwork-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('artwork-images')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const handleMultipleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const newUploadingImages: string[] = [];
    
    try {
      for (const file of files) {
        const tempId = `temp-${Math.random()}`;
        newUploadingImages.push(tempId);
        setUploadingImages(prev => [...prev, tempId]);
        
        const imageUrl = await uploadImage(file);
        if (imageUrl) {
          setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), imageUrl],
            image_url: prev.image_url || imageUrl // İlk fotoğrafı ana fotoğraf yap
          }));
        }
        
        setUploadingImages(prev => prev.filter(id => id !== tempId));
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (indexToRemove: number) => {
    setFormData(prev => {
      const newImages = prev.images?.filter((_, index) => index !== indexToRemove) || [];
      return {
        ...prev,
        images: newImages,
        image_url: newImages.length > 0 ? newImages[0] : undefined
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        images: formData.images || [],
        image_url: formData.images && formData.images.length > 0 ? formData.images[0] : formData.image_url
      };

      if (editingArtwork) {
        const { error } = await supabase
          .from('artworks')
          .update(submitData)
          .eq('id', editingArtwork.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('artworks')
          .insert([submitData]);

        if (error) throw error;
      }

      setFormData({
        title: '',
        artist: '',
        technique: 'oil',
        category: 'oil',
        size: '',
        price: '',
        year: '',
        materials: '',
        description: '',
        images: []
      });
      setShowAddForm(false);
      setEditingArtwork(null);
      fetchArtworks();
    } catch (error) {
      console.error('Error saving artwork:', error);
    }
  };

  const deleteArtwork = async (id: number) => {
    if (!confirm('Bu eseri silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('artworks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchArtworks();
    } catch (error) {
      console.error('Error deleting artwork:', error);
    }
  };

  const startEdit = (artwork: Artwork) => {
    setEditingArtwork(artwork);
    setFormData({
      ...artwork,
      images: artwork.images || []
    });
    setShowAddForm(true);
  };

  if (isLoading) {
    return (
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard requireAdmin={true}>
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center gap-8">
                <h1 className="text-3xl font-bold text-gray-900">Eser Yönetimi</h1>
                <nav className="flex items-center gap-6">
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Ana Sayfa
                  </button>
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/admin-orders')}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Sipariş Yönetimi
                  </button>
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/gallery')}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Galeri
                  </button>
                  <button 
                    onClick={() => {
                      window.REACT_APP_NAVIGATE('/');
                      setTimeout(() => {
                        const contactSection = document.getElementById('contact');
                        if (contactSection) {
                          contactSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 100);
                    }}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    İletişim
                  </button>
                </nav>
              </div>
              <button
                onClick={() => {
                  setShowAddForm(true);
                  setEditingArtwork(null);
                  setFormData({
                    title: '',
                    artist: '',
                    technique: 'oil',
                    category: 'oil',
                    size: '',
                    price: '',
                    year: '',
                    materials: '',
                    description: '',
                    images: []
                  });
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Yeni Eser Ekle
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">
                      {editingArtwork ? 'Eser Düzenle' : 'Yeni Eser Ekle'}
                    </h2>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className="ri-close-line text-2xl"></i>
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Eser Fotoğrafları (Birden fazla seçebilirsiniz)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleMultipleImageUpload}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        disabled={uploading}
                      />
                      {uploading && (
                        <p className="text-sm text-blue-600 mt-1">
                          Fotoğraflar yükleniyor... ({uploadingImages.length} adet)
                        </p>
                      )}
                      
                      {/* Yüklenen fotoğrafları göster */}
                      {formData.images && formData.images.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Yüklenen Fotoğraflar ({formData.images.length} adet):
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {formData.images.map((imageUrl, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={imageUrl}
                                  alt={`Fotoğraf ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeImage(index)}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 transition-colors cursor-pointer flex items-center justify-center"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                                {index === 0 && (
                                  <div className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                    Ana
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Eser Adı
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sanatçı
                        </label>
                        <input
                          type="text"
                          value={formData.artist}
                          onChange={(e) => setFormData(prev => ({ ...prev, artist: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teknik
                        </label>
                        <select
                          value={formData.technique}
                          onChange={(e) => setFormData(prev => ({ ...prev, technique: e.target.value, category: e.target.value === 'Yağlı Boya' ? 'oil' : 'watercolor' }))}
                          className="w-full p-2 border border-gray-300 rounded-lg pr-8"
                        >
                          <option value="Yağlı Boya">Yağlı Boya</option>
                          <option value="TUYB">TUYB</option>
                          <option value="Sulu Boya">Sulu Boya</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Boyut
                        </label>
                        <input
                          type="text"
                          value={formData.size}
                          onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                          placeholder="örn: 70x50 cm"
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fiyat
                        </label>
                        <input
                          type="text"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                          placeholder="örn: 5.000 TL+KDV"
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Yıl
                        </label>
                        <input
                          type="text"
                          value={formData.year}
                          onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                          placeholder="örn: 2023"
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Malzemeler
                      </label>
                      <input
                        type="text"
                        value={formData.materials}
                        onChange={(e) => setFormData(prev => ({ ...prev, materials: e.target.value }))}
                        placeholder="örn: Tuval üzerine yağlı boya"
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Açıklama
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        type="submit"
                        disabled={uploading}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-7
                        transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {editingArtwork ? 'Güncelle' : 'Kaydet'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        İptal
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Artworks Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {artworks.map((artwork) => (
              <div key={artwork.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden relative">
                  {artwork.image_url ? (
                    <img
                      src={artwork.image_url}
                      alt={artwork.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <i className="ri-image-line text-4xl text-gray-400"></i>
                    </div>
                  )}
                  {artwork.images && artwork.images.length > 1 && (
                    <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                      +{artwork.images.length - 1}
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-1">{artwork.title}</h3>
                  <p className="text-gray-600 text-sm mb-1">{artwork.artist}</p>
                  <p className="text-gray-500 text-xs mb-2">{artwork.technique} - {artwork.size}</p>
                  <p className="font-bold text-lg mb-3">{artwork.price}</p>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(artwork)}
                      className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded text-sm hover-bg-blue-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => deleteArtwork(artwork.id!)}
                      className="flex-1 bg-red-100 text-red-700 py-2 px-3 rounded text-sm hover-bg-red-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {artworks.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-image-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Henüz eser eklenmemiş</h3>
              <p className="text-gray-500 mb-4">İlk eserinizi ekleyerek başlayın</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-7

                transition-colors cursor-pointer whitespace-nowrap"
              >
                Eser Ekle
              </button>
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
}
